import React, { useCallback, useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import Ably from 'ably';

import { ErrorMessages } from '../../components/error/errorMessages';
import { PatientDetails } from '../../components/common/patientDetails';
import { DispenseRxData } from '../../components/common/dispenseRxData';
import { SearchIcon } from '../../assets/svg/icons';
import { useAllSelector } from '../../services/selectors/allSelector';
import { useAuthSelector } from '../../services/selectors/authSelector';
import { createRedBagHandover, fetchRedBag, fetchRedBagHandoverTargets } from '../../services/slices/dispense/redBag';
import { ErrorMessage, SuccessMessage } from '../../components/common/AllConfirmationMessages';

import * as Common from '../../components/common';
import * as Hooks from '../../hooks';
import * as Icons from '../../assets/svg/icons';
import * as Loading from '../../components/common/loader';
import * as routeNames from '../../routes/routeName';
import { apiKey } from '../../networking/urlEndPoints';

const getRedBagRxPanelColor = (rx) => {
	if (rx?.color) return rx.color;
	return '#B7E4BF';
};

const getReturnedChildDoses = (rx) => {
	return (rx?.dose_details || []).filter((dose) => (
		dose?.frequency_dose_id || dose?.dose_no || dose?.frequency
	));
};

const normalizeRedBagRxData = (rxData = []) => {
	return rxData.map((rx) => {
		const returnedChildDoses = getReturnedChildDoses(rx);

		return {
			...rx,
			id: rx.id,
			rxnumber: rx.rxnumber,
			drug: rx.drug,
			rxsig: rx.rxsig,
			refill_reqty: rx.quantity_returned || rx.total_rx_qty || '',
			return_handover_details: {
				returned_by: rx.handed_over_by_name,
				accepted_by: rx.accepted_by_name,
				accepted_at: rx.handover_at_display,
			},
			is_din_matched: {
				status: 1,
				color: getRedBagRxPanelColor(rx),
				is_fentanyl: 0,
			},
			investigate_dilaudid_delivery_all: returnedChildDoses.map((dose, doseIndex) => ({
				id: dose.frequency_dose_id || `${rx.id}-returned-dose-${doseIndex}`,
				dose: dose.quantity_returned || dose.dose || '',
				dose_no: dose.dose_no,
				frequency: dose.frequency,
				can_deliver: true,
				delivery_status: null,
			})),
		};
	});
};

const buildHandoverTargetOptions = (targets) => {
	const groups = [
		{ label: 'Assistant', items: targets?.drivers || [] },
		{ label: 'Pharmacist', items: targets?.pharmacists || [] },
		{ label: 'In-store Pharmacist', items: targets?.instore_pharmacist || [] },
	];
	const seen = new Set();

	return groups.flatMap((group) => (
		group.items
			.map((target) => ({
				...target,
				group: group.label,
				key: `${target.type}-${target.id}`,
			}))
			.filter((target) => {
				if (!target.id || !target.name || seen.has(target.key)) return false;
				seen.add(target.key);
				return true;
			})
	));
};

const RedBagHandoverSection = ({
	patient,
	targets,
	pharmacistDetail,
	onConfirm,
	loading,
	targetsLoading,
}) => {
	const sigRef = useRef(null);
	const [selectedSourcePharmacistId, setSelectedSourcePharmacistId] = useState('');
	const [selectedTargetKey, setSelectedTargetKey] = useState('');
	const [recipientType, setRecipientType] = useState('pharmacist');
	const [signatureData, setSignatureData] = useState(null);
	const [error, setError] = useState('');
	const targetOptions = buildHandoverTargetOptions(targets);
	const pharmacistOptions = targetOptions.filter((target) => (
		target.group !== 'Assistant' && String(target.id) !== String(selectedSourcePharmacistId)
	));
	const assistantOptions = targetOptions.filter((target) => target.group === 'Assistant');
	const recipientOptions = recipientType === 'assistant' ? assistantOptions : pharmacistOptions;

	useEffect(() => {
		setSelectedSourcePharmacistId('');
		setSelectedTargetKey('');
		setRecipientType('pharmacist');
		setSignatureData(null);
		setError('');
		sigRef.current?.clear();
	}, [patient?.id]);

	if (!patient) return null;

	const handleSignatureEnd = () => {
		const signature = sigRef.current?.toDataURL();
		setSignatureData(signature || null);
		if (signature) setError('');
	};

	const handleClearSignature = () => {
		sigRef.current?.clear();
		setSignatureData(null);
	};

	const handleConfirm = () => {
		const sourcePharmacist = pharmacistDetail?.find((pharmacist) => String(pharmacist.id) === String(selectedSourcePharmacistId));
		const target = targetOptions.find((option) => option.key === selectedTargetKey);
		if (!sourcePharmacist) {
			setError('Please select who is handing over this medication.');
			return;
		}
		if (!target) {
			setError('Please select who will receive this medication.');
			return;
		}
		if (!signatureData) {
			setError('Signature is required.');
			return;
		}
		setError('');
		onConfirm?.({ sourcePharmacist, target, signature_base64: signatureData });
	};

	return (
		<div className="mt-3" id={`redBagHandoverSection-${patient?.id}`}>
			{/* <div className="mb-2 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
				<div className="mb-4 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-800">
					Choose who is handing over the stock and who will receive it next.
				</div>
				<label className="mb-4 flex flex-col gap-1.5 text-sm font-medium text-slate-800">
					<span>Handing over</span>
					<select
						value={selectedSourcePharmacistId}
						onChange={(event) => {
							const nextSourcePharmacistId = event.target.value;
							setSelectedSourcePharmacistId(nextSourcePharmacistId);
							setSelectedTargetKey('');
							setError('');
						}}
						className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
						style={{ borderColor: '#e2e8f0' }}
						disabled={loading}
					>
						<option value="">Select pharmacist</option>
						{pharmacistDetail?.map((pharmacist) => (
							<option value={pharmacist.id} key={pharmacist.id}>
								{pharmacist.name}
							</option>
						))}
					</select>
				</label>
				<div>
					<p className="mb-1.5 text-sm font-medium text-slate-800">Send to</p>
					<div className="mb-3 grid grid-cols-2 gap-2">
						{[
							{ key: 'pharmacist', label: 'Pharmacist' },
							{ key: 'assistant', label: 'Assistant' },
						].map((option) => (
							<button
								key={option.key}
								type="button"
								onClick={() => {
									setRecipientType(option.key);
									setSelectedTargetKey('');
									setError('');
								}}
								disabled={loading || targetsLoading}
								className={`rounded-md border px-3 py-2 text-sm font-medium transition ${recipientType === option.key ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
							>
								{option.label}
							</button>
						))}
					</div>
					<select
						value={recipientOptions.some((target) => target.key === selectedTargetKey) ? selectedTargetKey : ''}
						onChange={(event) => {
							setSelectedTargetKey(event.target.value);
							setError('');
						}}
						className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
						style={{ borderColor: '#e2e8f0' }}
						disabled={targetsLoading || loading}
					>
						<option value="">{targetsLoading ? 'Loading recipients...' : `Select ${recipientType}`}</option>
						{recipientOptions.map((target) => (
							<option value={target.key} key={target.key}>
								{target.name}
							</option>
						))}
					</select>
				</div>
			</div>

			<div
				className="p-4 bg-gray-100 rounded-lg mb-3 border border-gray-200"
				style={{  borderColor: error && !signatureData ? 'red' : undefined }}
			>
				<div className="flex items-center justify-between">
					<h2 className="text-base text-gray-800">Signature</h2>
					<Icons.PharmacistSignatureIcon1 />
				</div>
				<div className="mt-3 relative w-full rounded-lg border border-gray-200 bg-white overflow-hidden min-h-[200px]">
					<SignatureCanvas
						ref={sigRef}
						penColor="black"
						clearOnResize={false}
						canvasProps={{ className: 'signature w-full', style: { minHeight: '200px' } }}
						onEnd={handleSignatureEnd}
					/>
					<button
						type="button"
						onClick={handleClearSignature}
						className="absolute bottom-2 right-2 bg-white hover:bg-gray-50 text-gray-800 text-xs font-medium uppercase px-3 py-1.5 rounded flex items-center gap-1.5 border border-gray-200"
					>
						<Icons.ClearPatientSignatureIcon />
						CLEAR
					</button>
				</div>
				{error && <p className="mt-2 text-sm text-red-600">{error}</p>}
			</div> */}


<div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
    {/* Left Side */}
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-4 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-800">
            Choose who is handing over the stock and who will receive it next.
        </div>

        <label className="mb-4 flex flex-col gap-1.5 text-sm font-medium text-slate-800">
            <span>Handing over</span>

            <select
                value={selectedSourcePharmacistId}
                onChange={(event) => {
                    const nextSourcePharmacistId = event.target.value;
                    setSelectedSourcePharmacistId(nextSourcePharmacistId);
                    setSelectedTargetKey('');
                    setError('');
                }}
                className="w-full rounded-md border font-normal border-gray-200 bg-white px-3 py-2.5 text-xs"
                disabled={loading}
            >
                <option value="">Select pharmacist</option>

                {pharmacistDetail?.map((pharmacist) => (
                    <option key={pharmacist.id} value={pharmacist.id}>
                        {pharmacist.name}
                    </option>
                ))}
            </select>
        </label>

        <p className="mb-2 text-sm font-medium">Send to</p>

        <div className="mb-3 grid grid-cols-2 gap-2">
            {[
                { key: "pharmacist", label: "Pharmacist" },
                { key: "assistant", label: "Assistant" },
            ].map((option) => (
                <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                        setRecipientType(option.key);
                        setSelectedTargetKey("");
                        setError("");
                    }}
                    className={`rounded-md border px-3 py-2 text-sm ${
                        recipientType === option.key
                            ? "border-blue-600 bg-blue-50 text-blue-700"
                            : "border-slate-200"
                    }`}
                >
                    {option.label}
                </button>
            ))}
        </div>

        <select
            value={
                recipientOptions.some(
                    (target) => target.key === selectedTargetKey
                )
                    ? selectedTargetKey
                    : ""
            }
            onChange={(e) => {
                setSelectedTargetKey(e.target.value);
                setError("");
            }}
            className="w-full rounded-md border border-gray-200 bg-white px-3 py-2.5 text-xs"
        >
            <option value="">
                {targetsLoading
                    ? "Loading recipients..."
                    : `Select ${recipientType}`}
            </option>

            {recipientOptions.map((target) => (
                <option key={target.key} value={target.key}>
                    {target.name}
                </option>
            ))}
        </select>
    </div>

    {/* Right Side */}
    <div
        className="rounded-lg border border-gray-200 bg-gray-100 p-4"
        style={{
            borderColor: error && !signatureData ? "red" : undefined,
        }}
    >
        <div className="flex items-center justify-between">
            <h2 className="text-base text-gray-800">Signature</h2>
            <Icons.PharmacistSignatureIcon1 />
        </div>

        <div className="relative mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white">
            <SignatureCanvas
                ref={sigRef}
                penColor="black"
                clearOnResize={false}
                canvasProps={{
                    className: "signature w-full h-40 md:h-44 xl:h-48",
                }}
                onEnd={handleSignatureEnd}
            />

            <button
                type="button"
                onClick={handleClearSignature}
                className="absolute bottom-2 right-2 rounded border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium uppercase"
            >
                CLEAR
            </button>
        </div>

        {error && (
            <p className="mt-2 text-sm text-red-600">{error}</p>
        )}
    </div>
</div>
			<button
				type="button"
				onClick={handleConfirm}
				disabled={loading || targetsLoading}
				className="px-4 w-full py-2 rounded-md text-white disabled:opacity-60"
				style={{ backgroundColor: '#10b981' }}
			>
				{loading ? 'Sending...' : 'Continue'}
			</button>
		</div>
	);
};

const RedBag = () => {
	const dispatch = Hooks.useCustomDispatch();
	const navigate = Hooks.useCustomNavigate();
	const { pharmacyId } = useAuthSelector();
	const { redBagResponse, pharmacistDetail } = useAllSelector();

	const [search, setSearch] = useState('');
	const [openIndex, setOpenIndex] = useState(null);
	const [selectedHandoverPatient, setSelectedHandoverPatient] = useState(null);
	const [selectedDoses, setSelectedDoses] = useState({});
	const [showSuccessToast, setShowSuccessToast] = useState(false);
	const [showErrorToast, setShowErrorToast] = useState(false);

	const fetchList = useCallback((searchText = '') => {
		if (!pharmacyId) return;

		dispatch(fetchRedBag({
			pharmacy_id: pharmacyId,
			search: searchText,
		}));
	}, [dispatch, pharmacyId]);

	useEffect(() => {
		fetchList('');
		return () => { };
	}, [fetchList]);

	useEffect(() => {
		if (!pharmacyId || !apiKey) return undefined;

		const ably = new Ably.Realtime({ key: apiKey });
		const channel = ably.channels.get('pharmacy-notifications');
		const eventName = `pharmacy-${pharmacyId}`;
		const handleHandoverUpdate = (message) => {
			const payload = message?.data || {};
			const isRedBagHandover = payload?.handover_type === 'red_bag';
			const isHandoverUpdate = payload?.type === 'delivery_handover_request'
				|| payload?.type === 'delivery_handover_response';

			if (isRedBagHandover && isHandoverUpdate) {
				setSelectedHandoverPatient(null);
				fetchList(search);
			}
		};

		channel.subscribe(eventName, handleHandoverUpdate);

		return () => {
			channel.unsubscribe(eventName, handleHandoverUpdate);
			// Ably reports an error when a still-connecting client is closed during React cleanup.
			if (ably.connection.state === 'connected') {
				ably.close();
			}
		};
	}, [fetchList, pharmacyId, search]);

	const handleSearchChange = (event) => {
		const value = event.target.value;
		setSearch(value);
		fetchList(value);
	};

	const handleDropdownToggle = (index) => {
		setSelectedHandoverPatient(null);
		setOpenIndex((previousIndex) => previousIndex === index ? null : index);
		setTimeout(() => {
			const nextPatientCard = document.getElementById(`patientCard-${index}`);
			if (nextPatientCard) {
				nextPatientCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		}, 100);
	};

	const handleOpenHandover = (patient) => {
		setSelectedHandoverPatient(patient);
		if (pharmacyId) {
			dispatch(fetchRedBagHandoverTargets({ pharmacy_id: pharmacyId }));
		}
		setTimeout(() => {
			document
				.getElementById(`redBagHandoverSection-${patient?.id}`)
				?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}, 100);
	};

	const handleConfirmHandover = async ({ sourcePharmacist, target, signature_base64 }) => {
		if (!selectedHandoverPatient?.patient_id || !pharmacyId) return;

		const winrxDataIds = (selectedHandoverPatient?.patients_win_rx_data || [])
			.map((rx) => rx.winrx_data_id)
			.filter(Boolean);
		const frequencyDoseIdsByWinrxId = (selectedHandoverPatient?.patients_win_rx_data || [])
			.reduce((doseIdsByWinrxId, rx) => {
				const winrxDataId = rx?.winrx_data_id;
				const returnedFrequencyDoseIds = (rx?.dose_details || [])
					.map((dose) => Number(dose?.frequency_dose_id))
					.filter((frequencyDoseId) => Number.isInteger(frequencyDoseId) && frequencyDoseId > 0);

				if (winrxDataId && returnedFrequencyDoseIds.length > 0) {
					doseIdsByWinrxId[String(winrxDataId)] = [...new Set(returnedFrequencyDoseIds)];
				}

				return doseIdsByWinrxId;
			}, {});

		if (winrxDataIds.length === 0) {
			setShowErrorToast('No RX records are available for handover.');
			setTimeout(() => setShowErrorToast(false), 3000);
			return;
		}

		try {
			await dispatch(createRedBagHandover({
				pharmacy_id: pharmacyId,
				patient_id: selectedHandoverPatient.patient_id,
				target_type: target.type,
				target_id: target.id,
				send_to_all_instore: false,
				winrx_data_ids: winrxDataIds,
				frequency_dose_ids_by_winrx_id: frequencyDoseIdsByWinrxId,
				message: `Returned stock handover from ${sourcePharmacist?.name || 'pharmacist'}`,
				source_pharmacist_id: sourcePharmacist?.id,
				signature_base64,
			})).unwrap();
			setSelectedHandoverPatient(null);
			setShowSuccessToast(true);
			setTimeout(() => setShowSuccessToast(false), 2500);
			fetchList(search);
		} catch (error) {
			const message =
				error?.data?.data?.error?.handover?.[0] ||
				error?.data?.message ||
				'Unable to hand over returned stock. Please try again.';
			setShowErrorToast(message);
			setTimeout(() => setShowErrorToast(false), 3000);
		}
	};

	const renderHandoverAction = (patient) => {
		const showHandoverSection = selectedHandoverPatient?.id === patient?.id;
		const pendingHandover = patient?.pending_red_bag_handover;

		if (pendingHandover) {
			return (
				<div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
					Handover request sent to {pendingHandover.recipient_name || 'the selected recipient'}.
				</div>
			);
		}

		if (showHandoverSection) {
			return (
				<RedBagHandoverSection
					patient={patient}
					targets={redBagResponse?.handoverTargets}
					pharmacistDetail={pharmacistDetail}
					onConfirm={handleConfirmHandover}
					loading={redBagResponse?.handoverLoading}
					targetsLoading={redBagResponse?.handoverTargetsLoading}
				/>
			);
		}

		return (
			<button
				type="button"
				onClick={() => handleOpenHandover(patient)}
				className="mt-3 w-full rounded-md px-4 py-2 text-white"
				style={{ backgroundColor: '#10b981' }}
			>
				Handover
			</button>
		);
	};

	return (
		<div className="app-wrapper flex justify-center">
			<div className="app-screen no-shades w-full h-full overflow-x-hidden">
				<div className="relative p-4">
					<div className="card bg-white p-4 rounded-lg shadow-sm">
						<div className="flex items-center justify-between gap-3">
							<Common.TopBar pharmacyId={pharmacyId} />
							<div className='flex items-center justify-center btnnn pt-4' onClick={() => navigate(`${routeNames.DASHBOARD_PHARMACY}`)} >
								<Icons.HomeIcon />
							</div>
						</div>
					</div>

					<div className="title-row my-2">
						<h1 className="text-xl font-bold">Returned Stock</h1>
					</div>

					<div className="relative mb-3">
						<div className="absolute left-3 top-1/2 -translate-y-1/2">
							<SearchIcon />
						</div>
						<input
							type="text"
							value={search}
							onChange={handleSearchChange}
							placeholder="Search Patients"
							className="w-full bg-white border border-gray-300 rounded-lg py-3 pl-10 pr-3 text-sm text-gray-700 focus:outline-none"
						/>
					</div>

					<div className="routes-list">
						{redBagResponse?.loading ? (
							<div className='flex justify-center h-96'>
								<Loading.LoadingView />
							</div>
						) : redBagResponse?.error !== null ? (
							<div className='flex justify-center pb-4 mb-4 h-96'>
								<ErrorMessages error={'Oops! Got an issue while fetching Data.Please try again!'} />
							</div>
						) : redBagResponse?.data?.length === 0 ? (
							<p className='flex justify-center pb-4 pt-3 h-96'>No returned medications are ready to hand over right now.</p>
						) : (
							redBagResponse?.data?.map((data, index) => (
								<div className="card bg-white px-4 py-3 rounded-lg shadow-sm mb-3" id={`patientCard-${index}`} key={data.id || index}>
									<PatientDetails
										data={data}
										patientImg={data.profile_image}
										totalRxData={data?.patients_win_rx_data?.length}
										index={index}
										openIndex={openIndex}
										onDropdownToggle={handleDropdownToggle}
										profileImageInputId={`redBagProfileImage-${data.id || index}`}
									>
										<DispenseRxData
											rxData={normalizeRedBagRxData(data?.patients_win_rx_data || [])}
											patientData={{ ...data, delivery_status: 'delivered' }}
											selectedDoses={selectedDoses}
											setSelectedDoses={setSelectedDoses}
											isActiveDeliverRxCard={openIndex === index}
											deliverRxSelectionEnabled={false}
										/>
										{renderHandoverAction(data)}
									</PatientDetails>
								</div>
							))
						)}
					</div>

				</div>
			</div>
			{showSuccessToast && (
				<SuccessMessage
					message="Returned stock handover request sent."
					setShowToast={() => setShowSuccessToast(false)}
				/>
			)}
			{showErrorToast && (
				<ErrorMessage
					message={typeof showErrorToast === 'string' ? showErrorToast : 'Unable to hand over returned stock. Please try again.'}
					setShowToast={() => setShowErrorToast(false)}
				/>
			)}
		</div>
	);
};

export default RedBag;
